/**
 * Bring the results region back into view when a listing changes page.
 *
 * WHY THIS IS NOT JUST `scrollIntoView` AFTER THE NAVIGATION: every shop
 * listing renders its grid behind a `v-if`/`v-else-if` on the loaded data,
 * so during the refetch the element the scroll would target does not
 * exist. The genre page already tried it that way —
 *
 *     await router.push(...)
 *     await nextTick()
 *     document.querySelector('.genre-section')?.scrollIntoView(...)
 *
 * — and the optional chain quietly swallowed the miss, which is why that
 * page looked like it handled pagination and did not: measured, clicking
 * page 2 left the reader at scrollY 2295 for the full 3s after the click.
 *
 * So the target is measured BEFORE the page changes, while it is still
 * mounted, and the scroll starts immediately. The reader travels up while
 * the new cards load, which also reads better than waiting for data and
 * then jumping.
 */
export function useResultsScroll() {
    /**
     * @param selector element marking the top of the results region
     * @param extraOffset additional gap below the sticky chrome
     */
    const scrollToResults = (selector = '.shop-results', extraOffset = 16) => {
        if (typeof window === 'undefined') return
        const el = document.querySelector(selector)
        if (!el) return

        // The shop header is sticky; landing the grid at viewport 0 would
        // tuck its first row underneath it.
        const chrome = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--cy-chrome-h')
        ) || 60
        const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - chrome - extraOffset)

        // Only ever travel UP. Paging while the results top is already on
        // screen should not yank the reader downwards.
        if (window.scrollY <= top + 4) return

        // `behavior: smooth` matches html { scroll-behavior: smooth } in
        // main.css, which that file already forces back to `auto` under
        // prefers-reduced-motion.
        window.scrollTo({ top, behavior: 'smooth' })

        // Measuring first has one cost: content ABOVE the results can
        // reflow while the new page loads, and then the position we aimed
        // at is stale. The genre hero does exactly that — 375px on page 1,
        // 293px on page 2 — which landed the grid 82px too high, tucked
        // under the sticky header. So re-measure once things settle and
        // correct.
        //
        // The correction is skipped when the anchor is nowhere near where
        // we aimed, which is the cheap way of saying "the reader has taken
        // over" — nobody gets yanked out of a scroll they started
        // themselves.
        const correct = () => {
            const settled = document.querySelector(selector)
            if (!settled) return
            const want = Math.max(0, settled.getBoundingClientRect().top + window.scrollY - chrome - extraOffset)
            if (Math.abs(want - top) <= 8) return
            if (Math.abs(window.scrollY - top) > 150) return
            window.scrollTo({ top: want, behavior: 'smooth' })
        }
        setTimeout(correct, 500)
        setTimeout(correct, 1200)
    }

    return { scrollToResults }
}
